import {AgentContext} from 'common/src/modules/shared/copilot/copilot.types';
import {CLStore} from 'common/src/redux/store';
import {
  AiContentVariationColumnDataType,
  RemoteAiContentProject,
  RemoteAiContentVariationColumn,
  RemoteAiContentContentFeedColumnParams,
} from '../../aiContentProject.types';
import {RemoteAiContentItem, AiContentItemOutputStatus} from '../../aiContentItem.types';
import {CreativeTemplate, CreativeTemplateFeature} from 'common/src/modules/creativeTemplates/creativeTemplates.types';
import {DynamicParametersType} from 'common/src/modules/dynamicParameters/dynamicParameters.enums';
import {Persona} from 'common/src/modules/brandGuidelines/brandGuidelines.types';
import {Folder} from 'common/src/modules/Assets/Assets.types';
import getAllAssetsFromTree from 'common/src/modules/Assets/utils/getAllAssetsFromTree';
import {taxonomy} from '@cl/shared';
import taxonomize from 'common/src/modules/taxonomy/utils/taxonomize';
import {TaxonomyType} from 'common/src/modules/taxonomy/taxonomy.enums';

type PersonaInfo = {
  id: number;
  name: string | null;
};

type AssetInfo = {
  id: number;
  name: string;
};

type AiContentProjectContextData = {
  project: RemoteAiContentProject | null;
  items: RemoteAiContentItem[];
  template: CreativeTemplate | null;
  projectPersonas: PersonaInfo[];
  projectAssets: AssetInfo[];
};

const VARIATION_COLUMN_TYPE_LABELS: Record<AiContentVariationColumnDataType, string> = {
  [AiContentVariationColumnDataType.Text]: 'Text',
  [AiContentVariationColumnDataType.Number]: 'Number',
  [AiContentVariationColumnDataType.Color]: 'Color',
  [AiContentVariationColumnDataType.Image]: 'Image',
  [AiContentVariationColumnDataType.Video]: 'Video',
  [AiContentVariationColumnDataType.Persona]: 'Persona',
  [AiContentVariationColumnDataType.Language]: 'Language',
};

const DYNAMIC_PARAM_TYPE_LABELS: Record<DynamicParametersType, string> = {
  [DynamicParametersType.Text]: 'Text',
  [DynamicParametersType.Color]: 'Color',
  [DynamicParametersType.FloatNumber]: 'Number',
  [DynamicParametersType.Image]: 'Image',
  [DynamicParametersType.Video]: 'Video',
  [DynamicParametersType.Boolean]: 'Boolean',
  [DynamicParametersType.Selector]: 'Selector',
  [DynamicParametersType.Audio]: 'Audio',
  [DynamicParametersType.Link]: 'Link',
  [DynamicParametersType.Widget]: 'Widget',
  [DynamicParametersType.Font]: 'Font',
  [DynamicParametersType.QR]: 'QR Code',
  [DynamicParametersType.Subtitles]: 'Subtitles',
};

const FEATURE_LABELS: Record<CreativeTemplateFeature, string> = {
  [CreativeTemplateFeature.Feed]: 'Product Feed',
  [CreativeTemplateFeature.Location]: 'Location',
  [CreativeTemplateFeature.Weather]: 'Weather',
  [CreativeTemplateFeature.Content]: 'Content',
  [CreativeTemplateFeature.Xenon]: 'Xenon',
  [CreativeTemplateFeature.PromoSlide]: 'Promo Slide',
  [CreativeTemplateFeature.Video]: 'Video',
};

const OUTPUT_STATUS_LABELS: Record<AiContentItemOutputStatus, string> = {
  [AiContentItemOutputStatus.Initial]: 'Initial',
  [AiContentItemOutputStatus.GeneratingPrompts]: 'Generating Prompts',
  [AiContentItemOutputStatus.PromptsGenerated]: 'Prompts Generated',
  [AiContentItemOutputStatus.GeneratingAssets]: 'Generating Assets',
  [AiContentItemOutputStatus.AssetsGenerated]: 'Assets Generated',
};

function formatBriefSection(
  project: RemoteAiContentProject,
  projectPersonas: PersonaInfo[],
  projectAssets: AssetInfo[],
): string {
  const brief = project.brief;
  const lines: string[] = ['### Project Brief'];

  lines.push(`- Objective: ${brief?.objective || 'Not set'}`);
  lines.push(`- Description: ${brief?.description || 'Not set'}`);
  lines.push(`- Languages: ${brief?.languages?.length ? brief.languages.join(', ') : 'None selected'}`);
  lines.push(`- Apply Brand Guidelines: ${brief?.applyBrandGuidelines ? 'Yes' : 'No'}`);

  // Format personas with ID and name
  if (projectPersonas.length > 0) {
    const personaList = projectPersonas
      .map((p) => `${p.name || 'Unnamed'} (ID: ${p.id})`)
      .join(', ');
    lines.push(`- Personas: ${projectPersonas.length} assigned - ${personaList}`);
  } else {
    lines.push(`- Personas: None assigned`);
  }

  // Format assets with ID and name
  if (projectAssets.length > 0) {
    const assetList = projectAssets
      .map((a) => `${a.name || 'Unnamed'} (ID: ${a.id})`)
      .join(', ');
    lines.push(`- Assets: ${projectAssets.length} assigned - ${assetList}`);
  } else {
    lines.push(`- Assets: None assigned`);
  }

  return lines.join('\n');
}

function formatInputParametersSection(project: RemoteAiContentProject): string {
  const columns = project.variationInputSchema?.columns || [];

  if (columns.length === 0) {
    return '### Input Parameters (Variation Columns)\nNo input parameters defined.';
  }

  const lines: string[] = ['### Input Parameters (Variation Columns)'];
  lines.push('The following input parameters are defined for item creation:');
  lines.push('| # | Name | Type | Required | Purpose |');
  lines.push('|---|------|------|----------|---------|');

  columns.forEach((column: RemoteAiContentVariationColumn, index: number) => {
    const typeName = VARIATION_COLUMN_TYPE_LABELS[column.dataType] || 'Unknown';
    lines.push(
      `| ${index + 1} | ${column.name || 'Unnamed'} | ${typeName} | ${column.isRequired ? 'Yes' : 'No'} | ${column.usedFor || 'Not specified'} |`,
    );
  });

  return lines.join('\n');
}

function formatTemplateSection(template: CreativeTemplate | null): string {
  if (!template) {
    return '### Template Information\nNo template selected.';
  }

  const lines: string[] = ['### Template Information'];
  lines.push(`- Template: ${template.name} (ID: ${template.id})`);
  lines.push(`- Type: ${template.typeName || 'Unknown'}`);

  const adSizes = template.formats?.map((f) => f.format.formatString).join(', ') || 'None';
  lines.push(`- Supported Ad Sizes: ${adSizes}`);

  return lines.join('\n');
}

function formatContentFeedColumnsSection(project: RemoteAiContentProject): string {
  const contentFeedColumns = project.templateMetadata?.contentFeedColumns?.columns;

  if (!contentFeedColumns || Object.keys(contentFeedColumns).length === 0) {
    return '### Content Feed Column Mappings\nNo content feed columns defined.';
  }

  const taxonomyMapping = taxonomy.taxonomyMappingsSubject.data?.[TaxonomyType.Content] || {};

  const lines: string[] = ['### Content Feed Column Mappings (Template Dynamic Elements)'];
  lines.push("The template's dynamic elements are mapped to these content feed columns:");
  lines.push('| # | Column Key | Name | Type | Purpose |');
  lines.push('|---|------------|------|------|---------|');

  let index = 1;

  Object.entries(contentFeedColumns).forEach(([key, params]: [string, RemoteAiContentContentFeedColumnParams]) => {
    const typeName = DYNAMIC_PARAM_TYPE_LABELS[params.type] || 'Unknown';
    const friendlyName = taxonomize(taxonomyMapping, key, key);
    lines.push(`| ${index} | ${key} | ${friendlyName} | ${typeName} | ${params.purpose || 'Not specified'} |`);
    index++;
  });

  return lines.join('\n');
}

function formatFeaturesSection(template: CreativeTemplate | null): string {
  const features = template?.features;

  if (!features || features.length === 0) {
    return '### Template Features\nNo special features configured.';
  }

  const featureNames = features.map((f) => FEATURE_LABELS[f] || f).join(', ');

  return `### Template Features\nThis template supports: ${featureNames}`;
}

function formatItemsSummarySection(
  project: RemoteAiContentProject,
  items: RemoteAiContentItem[],
): string {
  const columns = project.variationInputSchema?.columns || [];
  const columnNames = columns.map((c: RemoteAiContentVariationColumn) => c.name || 'Unnamed').join(', ');

  const lines: string[] = ['### Items Summary'];
  lines.push(`- Total Items: ${items.length}`);
  lines.push(`- Items Table Columns: id, name${columnNames ? ', ' + columnNames : ''}`);

  return lines.join('\n');
}

function formatItemsDataSection(
  project: RemoteAiContentProject,
  items: RemoteAiContentItem[],
): string {
  if (items.length === 0) {
    return '### Items Data\nNo items created yet.';
  }

  const columns = project.variationInputSchema?.columns || [];
  const columnNames = columns.map((c: RemoteAiContentVariationColumn) => c.name || 'unnamed');

  const lines: string[] = ['### Items Data (CSV)'];

  // Header row
  const headerRow = ['id', 'name', 'status', ...columnNames].join(',');
  lines.push(headerRow);

  // Data rows
  items.forEach((item) => {
    const inputValues = columnNames.map((colName: string) => {
      const value = item.inputValues[colName];

      if (value === null || value === undefined) {
        return '';
      }

      // Escape quotes and wrap in quotes if contains comma
      const stringValue = String(value);

      if (stringValue.includes(',') || stringValue.includes('"')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }

      return stringValue;
    });

    // Get overall item status (use first output status or Initial)
    const itemStatus = item.outputs?.length > 0
      ? OUTPUT_STATUS_LABELS[item.outputs[0].status] || 'Unknown'
      : 'Initial';

    const nameEscaped = item.name.includes(',') || item.name.includes('"')
      ? `"${item.name.replace(/"/g, '""')}"`
      : item.name;

    const row = [item.id, nameEscaped, itemStatus, ...inputValues].join(',');
    lines.push(row);
  });

  return lines.join('\n');
}

function aiContentProjectContext(store: CLStore): AgentContext<AiContentProjectContextData> {
  function getter(): AiContentProjectContextData {
    const state = store.getState();
    const project = state.aiContentProjectPage?.project ?? null;
    const allPersonas: Persona[] = state.brandGuidelinesPage?.brandPersonas?.data ?? [];

    // Get project assets from the project assets tree
    const projectAssetsTree: Folder | null = state.aiContentProjectPage?.projectAssets?.assetTree ?? null;

    // Filter personas to only those assigned to this project
    const projectPersonaIds = project?.brief?.personaIds ?? [];
    const projectPersonas: PersonaInfo[] = allPersonas
      .filter((persona) => projectPersonaIds.includes(persona.id))
      .map((persona) => ({
        id: persona.id,
        name: persona.name,
      }));

    // Get all assets from the project assets tree
    const projectAssets: AssetInfo[] = projectAssetsTree
      ? getAllAssetsFromTree(projectAssetsTree).map((asset) => ({
          id: asset.id,
          name: asset.name || 'Unnamed',
        }))
      : [];

    return {
      project,
      items: state.aiContentProjectPage?.items ?? [],
      template: state.aiContentProjectPage?.template ?? null,
      projectPersonas,
      projectAssets,
    };
  }

  function formatter({
    currentContext,
  }: {
    prevContext: AiContentProjectContextData | null;
    currentContext: AiContentProjectContextData;
  }): string | null {
    const {project, items, template, projectPersonas, projectAssets} = currentContext;

    if (!project) {
      return 'The user is not currently viewing an AI Content Project.';
    }

    const sections = [
      `## AI Content Project: ${project.name || 'Unnamed'} (ID: ${project.id})`,
      '',
      formatBriefSection(project, projectPersonas, projectAssets),
      '',
      formatInputParametersSection(project),
      '',
      formatTemplateSection(template),
      '',
      formatContentFeedColumnsSection(project),
      '',
      formatFeaturesSection(template),
      '',
      formatItemsSummarySection(project, items),
      '',
      formatItemsDataSection(project, items),
    ];

    return sections.join('\n');
  }

  return {
    title: 'AI Content Project',
    getter,
    formatter,
  };
}

export default aiContentProjectContext;
